import Button from 'components/Button';
import FormattedText from 'components/FormattedText';
import Icon from 'components/Icon';
import { i18nPath } from 'config/i18nNs';
import { Col, Row } from 'layout/Grid';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import {
  AuxTitle,
  Body1,
  Caption,
  Heading1,
  Heading2,
  Heading3
} from 'typography';
import { scrollToTop } from 'utils/scrollPage';
import { projectId } from 'utils/urlQueries';
import { getPathInfo, isHQEmployees } from 'utils/versionUtils';
import styles from './main.module.scss';

const { polVersion, market } = getPathInfo();

const Main: React.FC = () => {
  const { t } = useTranslation();

  const purposeLink = `./privacy?id=${projectId}#data-usage`;
  const collectedDataLink = `./privacy?id=${projectId}`;
  const dataTypes: Array<{ [key: string]: string }> = t(
    `${i18nPath.globalVariant}.content.collectedData.types`
  );
  const activities: Array<{ [key: string]: string }> = t(
    `${i18nPath.globalVariant}.activities`
  );

  const overview: {
    title: string;
    blocks: Array<{ icon?: string; page: string; title: string }>;
  } = t(`${i18nPath.globalVariant}.content.overview`);

  return (
    <>
      <section>
        <Row>
          <Col sm={12} className="column">
            {polVersion === 'employees' && (
              <AuxTitle>
                {t(`${i18nPath.globalVariant}.content.policyTitle`)}
              </AuxTitle>
            )}
            <Heading1>
              <span>{t(`${i18nPath.globalVariant}.content.title`)}</span>
            </Heading1>
            {isHQEmployees(projectId, market) ? (
              <FormattedText
                i18nKey={`${i18nPath.globalVariant}.content.introHQ`}
              />
            ) : (
              <FormattedText
                i18nKey={`${i18nPath.globalVariant}.content.intro`}
              />
            )}
          </Col>
        </Row>

        <Row
          columnGap={45}
          rowGap={80}
          mobileColumnGap={16}
          className={styles.introDetails}
        >
          <Col sm={12} md={6} className="column">
            <Heading3>
              {t(`${i18nPath.globalVariant}.content.purpose.title`)}
            </Heading3>
            <Body1>
              {t(`${i18nPath.globalVariant}.content.purpose.intro`)}
            </Body1>

            {activities &&
              Array.isArray(activities) &&
              activities.map((item, index) => (
                <Link
                  className={styles.activitiesLink}
                  to={`./privacy/${item.tabId}?id=${projectId}`}
                  key={`purpose${index}`}
                >
                  <Body1 hasIcon>
                    <Icon name="icon-diagonal-up-right" size={18} />
                    {item.label}
                  </Body1>
                </Link>
              ))}

            <Body1>
              {t(`${i18nPath.globalVariant}.content.purpose.caption.title`)}
            </Body1>
            <Caption>
              {t(
                `${i18nPath.globalVariant}.content.purpose.caption.description`
              )}
            </Caption>

            <div className={styles.moreLink}>
              <Link to={purposeLink}>
                <Button tabIndex={-1} variant="secondary">
                  {t(`${i18nPath.dictionary}.knowMore`)}
                </Button>
              </Link>
            </div>
          </Col>

          <Col sm={12} md={6} className="column">
            <article className={styles.article}>
              <Heading3>
                {t(`${i18nPath.globalVariant}.content.collectedData.title`)}
              </Heading3>
              <Body1>
                {t(`${i18nPath.globalVariant}.content.collectedData.intro`)}
              </Body1>
              {dataTypes &&
                Array.isArray(dataTypes) &&
                dataTypes.map((item, index) => (
                  <Body1 key={`dataType${index}`} hasIcon>
                    {item.icon && <Icon name={item.icon} />}
                    {item.label}
                  </Body1>
                ))}

              <div className={styles.moreLink}>
                <Link to={collectedDataLink}>
                  <Button tabIndex={-1} variant="secondary">
                    {t(`${i18nPath.dictionary}.knowMore`)}
                  </Button>
                </Link>
              </div>
            </article>

            <article className={styles.article}>
              <Heading3>
                {t(`${i18nPath.globalVariant}.content.contact.title`)}
              </Heading3>
              <FormattedText
                i18nKey={`${i18nPath.globalVariant}.content.contact.intro`}
              />
            </article>
          </Col>
        </Row>
      </section>

      {overview &&
        typeof overview === 'object' &&
        polVersion !== 'employees' && (
          <section className={styles.overview}>
            <Heading1>
              {t(`${i18nPath.globalVariant}.content.overview.title`)}
            </Heading1>
            <Row
              className={styles.overviewContent}
              rowGap={80}
              columnGap={45}
              mobileColumnGap={16}
            >
              {overview.blocks &&
                overview.blocks.map((policy, index) => (
                  <Col sm={12} md={6} className="column" key={`policy${index}`}>
                    <Heading2 id={`${policy.title}-${index}`}>
                      <Icon name={policy.icon} size={24} />
                      {policy.title}
                    </Heading2>

                    <FormattedText
                      i18nKey={`${i18nPath.globalVariant}.content.overview.blocks.${index}.content`}
                    />

                    <div className={styles.actions}>
                      <Link
                        aria-labelledby={`${policy.title}-${index}`}
                        to={`./${policy.page}?id=${projectId}`}
                      >
                        <Button tabIndex={-1} variant="secondary">
                          {t(`${i18nPath.dictionary}.knowMore`)}
                        </Button>
                      </Link>
                    </div>
                  </Col>
                ))}
            </Row>
          </section>
        )}

      {polVersion !== 'employees' && (
        <div className={styles.bottomBtn}>
          <Button size="large" onClick={scrollToTop}>
            <Icon name="icon-arrow-up" size={16} />
            {t(`${i18nPath.dictionary}.backToTop`)}
          </Button>
        </div>
      )}
    </>
  );
};

export default Main;
